import { useEffect, useRef, useState } from "react";
import { Copy, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "../../lib/api";
import { Button, Card, ErrorBox, Field, Input, Spinner } from "../../components/kd";

function lowContrast(hex: string): boolean {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return false;
  const ch = (i: number) => { const c = parseInt(hex.slice(i, i + 2), 16) / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  const l = 0.2126 * ch(1) + 0.7152 * ch(3) + 0.0722 * ch(5);
  return 1.05 / (l + 0.05) < 4.5;
}

const KEYS = ["name", "strasse", "plz", "ort", "telefon", "email", "primaerfarbe", "slug"];

export function PraxisSettings() {
  const [p, setP] = useState<any>(null);
  const [form, setForm] = useState<any>({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => api.get("/admin/praxis").then((r) => { setP(r.data); setForm(Object.fromEntries(KEYS.map((k) => [k, r.data[k] || ""]))); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true); setErr("");
    try { await api.put("/admin/praxis", form); toast.success("Praxisdaten gespeichert."); load(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const uploadLogo = async (f?: File) => {
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Das Logo darf maximal 2 MB groß sein.");
    const fd = new FormData(); fd.append("file", f);
    try { await api.post("/admin/praxis/logo", fd); toast.success("Logo gespeichert."); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const downloadQr = async () => {
    try {
      const r = await api.get("/admin/praxis/qr-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `QR-Code_${p.slug}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Der QR-Code konnte nicht erstellt werden."); }
  };

  if (!p) return <Spinner />;
  const f = (k: string, label: string, extra: any = {}) => (
    <Field label={label} htmlFor={`p-${k}`} required={k === "name" || k === "slug" || k === "primaerfarbe"} hint={extra.hint}>
      <Input id={`p-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`praxis-${k}-input`} {...extra.input} />
    </Field>
  );
  return (
    <div className="grid gap-5 lg:grid-cols-5">
      <Card className="lg:col-span-3">
        <h2 className="mb-4">Praxisdaten</h2>
        <ErrorBox testid="praxis-error">{err}</ErrorBox>
        {f("name", "Praxisname")}
        {f("strasse", "Straße")}
        <div className="grid grid-cols-3 gap-3"><div>{f("plz", "PLZ")}</div><div className="col-span-2">{f("ort", "Ort")}</div></div>
        <div className="grid gap-x-3 sm:grid-cols-2">{f("telefon", "Telefon")}{f("email", "E-Mail (erhält Antworten auf Konsildent-Mails)")}</div>
        <div className="grid gap-x-3 sm:grid-cols-2">
          <Field label="Primärfarbe" htmlFor="p-farbe" required hint={lowContrast(form.primaerfarbe) ? "Zu hell für ausreichenden Kontrast (unter 4,5:1) – verwendet wird automatisch #0B5C75." : "Hex-Wert, z. B. #0B5C75"}>
            <div className="flex gap-2">
              <input type="color" aria-label="Farbwähler" value={/^#[0-9a-fA-F]{6}$/.test(form.primaerfarbe) ? form.primaerfarbe : "#0B5C75"} onChange={(e) => setForm({ ...form, primaerfarbe: e.target.value.toUpperCase() })} className="h-11 w-14 cursor-pointer rounded-lg border border-border" data-testid="praxis-color-picker" />
              <Input id="p-farbe" value={form.primaerfarbe} onChange={(e) => setForm({ ...form, primaerfarbe: e.target.value })} data-testid="praxis-primaerfarbe-input" />
            </div>
          </Field>
          {f("slug", "Slug (Adresse des Formulars)", { hint: "Kleinbuchstaben, Ziffern, Bindestriche" })}
        </div>
        <Button onClick={save} loading={busy} data-testid="praxis-save-button">Speichern</Button>
      </Card>
      <div className="space-y-5 lg:col-span-2">
        <Card>
          <h2 className="mb-4">Logo</h2>
          {p.logo_url ? <img src={fileUrl(p.logo_url)} alt="Praxislogo" className="mb-4 max-h-20 object-contain" data-testid="praxis-logo-preview" /> : <p className="mb-4 text-text-muted">Noch kein Logo hinterlegt.</p>}
          <input ref={fileRef} type="file" hidden accept=".png,.jpg,.jpeg,.svg" onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }} data-testid="logo-file-input" />
          <Button variant="secondary" onClick={() => fileRef.current?.click()} data-testid="logo-upload-button"><Upload className="h-4 w-4" aria-hidden />Logo hochladen</Button>
          <p className="mt-2 text-sm text-text-muted">PNG, JPG oder SVG, max. 2 MB</p>
        </Card>
        <Card>
          <h2 className="mb-2">Überweisungslink</h2>
          <code className="mb-3 block break-all rounded-md bg-bg p-2 text-sm" data-testid="referral-link">{p.ueberweisungslink}</code>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => { navigator.clipboard?.writeText(p.ueberweisungslink); toast.success("Link kopiert."); }} data-testid="copy-link-button"><Copy className="h-4 w-4" aria-hidden />Kopieren</Button>
            <Button onClick={downloadQr} data-testid="qr-download-button"><Download className="h-4 w-4" aria-hidden />QR-Code als PDF herunterladen</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
